import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Home, Bug, Mail } from 'lucide-react';
import { errorLogger, AppError, ErrorType } from '../../utils/errorHandling';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    errorCount: 0
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    
    // Log błędu do systemu
    const appError = new AppError(
      error.message,
      ErrorType.CLIENT,
      undefined,
      { componentStack: errorInfo.componentStack }
    );
    errorLogger.log(appError);
    
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));
  }

  private handleReset = () => {
    // Jeśli było więcej niż 3 błędy, przeładuj całą stronę
    if (this.state.errorCount > 3) {
      window.location.reload();
    } else {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null
      });
    }
  };

  private handleGoHome = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-zinc-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-zinc-800 rounded-2xl p-8 text-center">
            <div className="inline-flex p-4 bg-red-500/20 rounded-full mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">
              Ups! Coś poszło nie tak
            </h1>
            
            <p className="text-gray-400 mb-6">
              Wystąpił nieoczekiwany błąd. Możesz spróbować odświeżyć stronę lub wrócić do strony głównej.
            </p>
            
            {/* Szczegóły błędu tylko w trybie development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 p-4 bg-zinc-700 rounded-lg text-left">
                <div className="flex items-center gap-2 mb-2">
                  <Bug className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-red-400 font-semibold">Szczegóły błędu (widoczne tylko w trybie deweloperskim)</span>
                </div>
                <p className="text-sm text-red-400 font-mono">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <pre className="text-xs text-gray-500 mt-2 overflow-auto max-h-40">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Liczba błędów w tej sesji: {this.state.errorCount}
                </p>
              </div>
            )}
            
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg flex items-center gap-2 transition-all"
              >
                <RefreshCw className="w-4 h-4" />
                Odśwież stronę
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg flex items-center gap-2 transition-all"
              >
                <Home className="w-4 h-4" />
                Strona główna
              </button>
            </div>
            
            {/* Link do zgłoszenia błędu */}
            <div className="mt-6 pt-6 border-t border-zinc-700">
              <a
                href="mailto:support@plexisystem.pl?subject=Błąd w aplikacji"
                className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4" />
                Zgłoś problem
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}